import React, { useState, useEffect } from 'react';
import HeaderMain from "../components/header/HeaderMain";
import NavMain from "../components/nav/NavMain";
import axios from 'axios';
import income from "../income.json";
import expense from "../expense.json";
import GLOBALVAR from "../constants"; // Import GLOBALVAR constant
import { Table, Button } from "react-bootstrap";
import ToPeso from '../components/misc/ToPeso';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import LoadingSpinner from '../components/misc/LoadingSpinner';

const jsonDataIncome = income;
const jsonDataExpense = expense;

function IncomeStatement() {
    const [dateSelect, setDateSelect] = useState('');
    const [dateHeader, setDateHeader] = useState('');
    const [error, setError] = useState('');
    const [items, setItems] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [totalExpense, setTotalExpense] = useState(0);
    const [netIncome, setNetIncome] = useState(0);
    const [showSpinner, setShowSpinner] = useState(false);

    // Flatten the income and expense JSON data into a single array
    const flattenData = (data, type) => {
        const result = [];
        const traverse = (obj, path = '') => {
            if (Array.isArray(obj)) {
                obj.forEach(item => {
                    result.push({ ...item, type, path: `${path} > ${item.label}` });
                });
            } else if (typeof obj === 'object' && obj !== null) {
                Object.keys(obj).forEach(key => {
                    traverse(obj[key], `${path} > ${key}`);
                });
            }
        };
        traverse(data);
        return result;
    };

    const incomeItems = flattenData(jsonDataIncome.Income, 'Income');
    const expenseItems = flattenData(jsonDataExpense.Expense, 'Expense');
    const allItems = [...incomeItems, ...expenseItems];

    useEffect(() => {
        if (items.length > 0) {
            const revenue = items
                .filter(item => item.type === 'Income')
                .reduce((acc, curr) => acc + parseFloat(curr.balance || 0), 0);
            const expense = items
                .filter(item => item.type === 'Expense')
                .reduce((acc, curr) => acc + parseFloat(curr.balance || 0), 0);
            
            // Ensure the totals are floats
            setTotalRevenue(parseFloat(revenue.toFixed(2))); // Round to 2 decimal places
            setTotalExpense(parseFloat(expense.toFixed(2))); // Round to 2 decimal places
            setNetIncome(parseFloat((revenue - expense).toFixed(2))); // Round to 2 decimal places
        }
    }, [items]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setShowSpinner(true);
    
        if (!dateSelect) {
            setError('Select Date is required.');
            setShowSpinner(false);
            return;
        }
    
        try {
            setError('');
            const itemIds = allItems.map(item => item.id);
    
            const [incomeResponse, expenseResponse] = await Promise.all([
                axios.get(`${GLOBALVAR["SERVER_ROOT"]}/api/income-total/itemdate/multi`, { params: { item_ids: itemIds, date: dateSelect } }),
                axios.get(`${GLOBALVAR["SERVER_ROOT"]}/api/expense-total/itemdate/multi`, { params: { item_ids: itemIds, date: dateSelect } })
            ]);
    
            const incomeData = incomeResponse.data || {};
            const expenseData = expenseResponse.data || {};
    
            const updatedItems = allItems.map(item => ({
                ...item,
                balance: (item.type === 'Income' ? incomeData[item.id] : expenseData[item.id]) || 0
            }));
    
            setItems(updatedItems);
    
            const dateObj = new Date(dateSelect);
            const formattedDate = dateObj.toLocaleDateString('en-US', {
                month: 'long',
                day: '2-digit',
                year: 'numeric'
            });
    
            setDateHeader(formattedDate);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError('Error fetching transactions. Please try again.');
        } finally {
            setShowSpinner(false);
        }
    };    

    const handleExportPDF = async () => {
        setShowSpinner(true);

        try {
            const existingPdfBytes = await fetch("/pdftemplates/income-statement.pdf").then(res =>
                res.arrayBuffer()
            );

            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

            const pages = pdfDoc.getPages();
            const firstPage = pages[0];
            const secondPage = pages[1];
            const thirdPage = pages[2];

            const centerText = (text) => {
                const textWidth = helveticaFont.widthOfTextAtSize(text, 9);
                const pageWidth = firstPage.getWidth();
                return (pageWidth - textWidth) / 2;
            };

            const today = new Date();
            const dateTimeString = `${today.toLocaleString('default', { month: 'long' })} ${today.getDate()}, ${today.getFullYear()} ${today.getHours() % 12 || 12}:${today.getMinutes().toString().padStart(2, '0')}:${today.getSeconds().toString().padStart(2, '0')} ${today.getHours() >= 12 ? 'PM' : 'AM'}`;

            firstPage.drawText('As of', { x: centerText('As of'), y: 687, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
            firstPage.drawText(dateHeader, { x: centerText(dateHeader), y: 675, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });

            const coordY = {
                0:619,
                5:533,
                19:350,
                33:167,
                44:703, // second page first item
                47:654,
                61:458,
                75:275,
                76:249,
                77:225,
                80:122,
                81:98,
                83:48,
                84:703,
                86:666,
                89:617,
                90:592,
                112:310,
                118:225
            };

            if (items.length > 0) {
                let coordsFlag = 0;

                items.forEach((item, index) => {
                    const balCon = parseFloat(item.balance);
                    const balance = balCon.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    if(index <= 43){
                        coordsFlag = (coordY[index])?coordY[index]:coordsFlag - 12.25;

                        firstPage.drawText(balance, { x: 458, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if(index > 43 && index <= 83){
                        coordsFlag = (coordY[index])?coordY[index]:coordsFlag - 12.25;

                        secondPage.drawText(balance, { x: 458, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }

                    if(index > 83 && index <= 118){
                        coordsFlag = (coordY[index])?coordY[index]:coordsFlag - 12.25;

                        thirdPage.drawText(balance, { x: 458, y: coordsFlag, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
                    }
                });

                thirdPage.drawText(dateTimeString, { x: 441, y: 86, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });

                thirdPage.drawText(totalRevenue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 167, y: 124, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                secondPage.drawText(totalRevenue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 458, y: 167, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                
                thirdPage.drawText(totalExpense.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 167, y: 113, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                thirdPage.drawText(totalExpense.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 458, y: 190, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });

                thirdPage.drawText(netIncome.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), { x: 167, y: 99, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
                
            }

            const pdfBytes = await pdfDoc.save();
            saveAs(new Blob([pdfBytes]), `Income_Statement_${Math.floor(new Date(dateHeader).getTime() / 1000)}.pdf`);
        } catch (err) {
            console.error("Error generating PDF:", err);
            setError('Error generating PDF. Please try again.');
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <>
            {showSpinner && <LoadingSpinner />}
            <NavMain slug="income-statement" parent="3" />
            <HeaderMain slug="income-statement" />
            <div className="main-content financials">
                <div className="container">
                    <h4>Retrieve Income Statement</h4>
                    <form onSubmit={handleSubmit} className="mb-4">
                        <div className="mb-3">
                            <label htmlFor="dateSelect" className="form-label">Select Date</label>
                            <input
                                type="date"
                                className="form-control"
                                id="dateSelect"
                                value={dateSelect}
                                onChange={(e) => setDateSelect(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="btn btn-primary">Retrieve</button>
                    </form>
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="date-selected">{dateHeader}</div>
                    {items.length > 0 && (
                        <>
                            <Table className="list-container table table-hover" responsive>
                                <thead>
                                    <tr>
                                        <th className="list-header-item">Type of Account</th>
                                        <th className="list-header-item">GL Account Description</th>
                                        <th className="list-header-item">Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => (
                                        <tr key={index} className="list-body">
                                            <td className="list-body-item">{item.type}</td>
                                            <td className="list-body-item">{item.label}</td>
                                            <td className="list-body-item"><ToPeso amount={item.balance} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="list-body">
                                        <td className="list-body-item" colSpan="2">Total Revenue</td>
                                        <td className="list-body-item"><ToPeso amount={totalRevenue} /></td>
                                    </tr>
                                    <tr className="list-body">
                                        <td className="list-body-item" colSpan="2">Total Expense</td>
                                        <td className="list-body-item"><ToPeso amount={totalExpense} /></td>
                                    </tr>
                                    <tr className="list-body">
                                        <td className="list-body-item" colSpan="2">Net Income</td>
                                        <td className="list-body-item"><ToPeso amount={netIncome} /></td>
                                    </tr>
                                </tfoot>
                            </Table>
                            <Button variant="primary" onClick={handleExportPDF}>
                                Export PDF
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default IncomeStatement;